import { FC, useState } from 'react';
import styles from './styles.module.scss';
import Status from '@components/common/status';
import { IModalComponentProps } from '@context/modal_context';
import {
    ETransactionAction,
    ETransactionStatus,
    ETransactionType,
    EUserType,
    TGetOrderById,
    TOrderDetail,
    TTransaction
} from '@xeppt/xeppt-sdk/types';
import type { TOrderWithReferenceCode } from 'src/types/reference_code';
import type { TOrderDetailTopLevelFields } from '../../../../types/order_detail';
import { mergeOrderDetail } from '../../../../types/order_detail';
import { formatDate, prettifyAmount } from '@utils/index';
import Typography from '@components/common/typography';
import { Icon } from '@components/icons';
import Dropdown from '@components/common/dropdown';
import { useApiQuery } from '@hooks/api/useApiQuery';
import {
    apiTransactionsService,
    apiOrderService,
    apiOrderBlockService,
    OrderBlockApiError
} from '@api';
import useModalContext from '@hooks/context/useModalContext';
import { useUserContext } from '@hooks/context/useUserContext';
import { modalIds } from '@const/modals';
import Button from '@components/common/button';
import { useHelpCenterModals } from '@hooks/modals/useHelpCenterModals';
import { useLocales } from '@hooks/helpers/useLocales';
import { showToast } from '@components/toast';
import RefundOrderSubview from './RefundOrderSubview';
import BlockCustomerSubview from './BlockCustomerSubview';

// ─── Types ───────────────────────────────────────────────────────────────────

// Fields the backend's ordersMgmtItem genuinely nests under `order` (SDK lag —
// see src/types/order_detail.ts's file header for the sibling fields it does
// NOT nest, which must never be added here).
type TOrderNested = TOrderWithReferenceCode & {
    id: string;
    currency?: string;
    environment?: string;
    customer_display_name?: string;
    customer_avatar_url?: string;
    customer_id?: string;
};

// Fully-populated order shape used everywhere below once the detail
// envelope's top-level refund/breakdown fields have been merged onto it via
// mergeOrderDetail() — see src/types/order_detail.ts for why those fields
// live at the detail-envelope level, not on the nested order, per the backend.
type TOrderWithId = TOrderNested & TOrderDetailTopLevelFields;

type TOrderDetailWithDesc = TOrderDetail & { description?: string };

// Raw shape of the GET /orders/{id} response, BEFORE mergeOrderDetail() has
// relocated its top-level refund/breakdown fields onto `order`. Never read
// order_breakdown/refunded_amount/remaining_amount/refunds directly off this
// type's `order` — they aren't there yet; they live on the envelope itself.
type TOrderDetailApiResponse = Omit<TGetOrderById, 'order'> &
    TOrderDetailTopLevelFields & { order: TOrderNested };

type OrderTab = 'details' | 'items' | 'transactions';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function nameInitials(displayName: string): string {
    const parts = displayName.trim().split(/\s+/);
    if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return displayName.slice(0, 2).toUpperCase();
}

function handleInitials(handle: string): string {
    const clean = handle.replace(/^@/, '');
    if (!clean) return '??';
    const parts = clean.split(/[._-]/);
    if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return clean.slice(0, 2).toUpperCase();
}

function orderStatusLabel(status: string): string {
    switch (status) {
        case 'succeeded': return 'Paid';
        case 'pending': return 'Pending';
        case 'cancelled': return 'Canceled';
        case 'refunded': return 'Refunded';
        case 'partially_refunded': return 'Partially refunded';
        case 'expired': return 'Expired';
        default: return status;
    }
}

function orderStatusVariant(
    status: string
): 'success' | 'warning' | 'error' | 'gray' {
    switch (status) {
        case 'succeeded': return 'success';
        case 'pending': return 'warning';
        case 'cancelled':
        case 'expired': return 'error';
        default: return 'gray';
    }
}

// Merchant receives money on DEBIT txns (customer's wallet debit = merchant credit).
function txnColorClass(txn: TTransaction): string {
    if (
        txn.status === ETransactionStatus.CANCELED ||
        txn.status === ETransactionStatus.DECLINED
    ) {
        return styles.txn_amount_cancel;
    }
    if (txn.type === ETransactionType.DEBIT) {
        return styles.txn_amount_green;
    }
    return '';
}

type TMergedTxnRow = {
    id: string;
    created_at: Date;
    amountLabel: string;
    colorClass: string;
};

// The backend's `transactions` array is now itself the unified ledger for
// this order (charge leg + every refund leg, in one array — see
// order_business_detail.go's GetOrderBusinessDetail, 2026-07-13 merchant-tabs
// redesign). No separate `order.refunds` merge is needed any more: every row
// (purchase or refund) is a real transaction with a real transaction id, so
// one mapping covers both. A refund leg is CreditType, which already renders
// as a minus in the default (non-green, non-red) color via the existing
// type-based sign/color rules below — matching the Figma spec ("refund
// amounts are not red and not green") with no extra branch needed.
// Sorted oldest-first: the Figma spec shows one dated line-item list telling
// the order's story chronologically, not newest-first.
export function buildMergedTxnRows(transactions: TTransaction[] | undefined): TMergedTxnRow[] {
    const rows: TMergedTxnRow[] = (transactions ?? []).map((txn) => ({
        id: txn.id,
        // txn.created_at is typed Date by the SDK but arrives as an ISO string
        // over JSON at runtime — normalize so the sort comparator's .getTime()
        // is always called on a real Date.
        created_at: new Date(txn.created_at),
        amountLabel: `${txn.type === ETransactionType.DEBIT ? '+' : '−'}$${prettifyAmount(
            Number(txn.amount).toFixed(2)
        )}`,
        colorClass: txnColorClass(txn)
    }));
    return rows.sort((a, b) => a.created_at.getTime() - b.created_at.getTime());
}

// Resolves the transaction id of the chronologically-first refund leg on this
// order, for the "View refund" banner link below — which needs a real
// transaction id to pass to setPendingTxnId (it re-fetches by id via
// getTransactionById). order.refunds[].id is a merchant_refunds row id — a
// DIFFERENT id, in a different table, from the refund's own wallet-transaction
// leg — so it was never a valid getTransactionById argument. Fixed here as a
// direct consequence of switching to the unified transactions array, which
// carries the refund leg's own (correct) id.
export function firstRefundTransactionId(transactions: TTransaction[] | undefined): string | undefined {
    return (transactions ?? [])
        .filter((txn) => txn.action === ETransactionAction.REFUND)
        // created_at is an ISO string at runtime (SDK types it Date) — wrap in
        // new Date() before .getTime() on these raw transaction rows.
        .sort(
            (a, b) =>
                new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        )[0]?.id;
}

const ORDER_TABS: { label: string; value: OrderTab }[] = [
    { label: 'Details', value: 'details' },
    { label: 'Items', value: 'items' },
    { label: 'Transactions', value: 'transactions' }
];

// ─── Component ───────────────────────────────────────────────────────────────

const ViewOrderModal: FC<
    IModalComponentProps<{ order: TGetOrderById; refetchOrders?: () => void }>
> = ({ onClose, modalData }) => {
    // All hooks must be declared before any conditional return.
    const { onOpen, changeModalData } = useModalContext();
    const { user } = useUserContext();
    const [activeTab, setActiveTab] = useState<OrderTab>('details');
    const [pendingTxnId, setPendingTxnId] = useState('');
    const [isRefundOpen, setIsRefundOpen] = useState(false);
    const [refundJustSucceeded, setRefundJustSucceeded] = useState(false);
    const [isBlockOpen, setIsBlockOpen] = useState(false);
    const [isBlockSubmitting, setIsBlockSubmitting] = useState(false);
    const [imgError, setImgError] = useState(false);
    const { handleOpenSubmitRequestModal } = useHelpCenterModals();
    const { requestErrorLocale, submitLocale } = useLocales();

    // Re-open this order's details drawer when the user clicks the back arrow from a
    // transaction opened via the Transactions tab below. Captures the order + refetch
    // callback so the closure still points at the right data once this component
    // unmounts in favor of the transaction drawer (same reopen pattern as
    // view_transaction's own reopenCurrentTxn).
    const reopenCurrentOrder = () => {
        changeModalData({ order: modalData?.order, refetchOrders: modalData?.refetchOrders });
        onOpen({ modalId: modalIds.VIEW_ORDER });
    };

    useApiQuery({
        method: () => apiTransactionsService.getTransactionById(pendingTxnId),
        isInitialRequest: false,
        condition: !!pendingTxnId,
        deps: [pendingTxnId],
        onSuccess: (txn) => {
            changeModalData({
                transaction: txn,
                refetchTransactions: () => {},
                onBack: reopenCurrentOrder
            });
            onOpen({ modalId: modalIds.VIEW_TRANSACTION });
            setPendingTxnId('');
        },
        onError: (e) => {
            requestErrorLocale(e);
            setPendingTxnId('');
        }
    });

    // Guard after all hooks.
    if (!modalData?.order) return null;

    const detail = modalData.order as TOrderDetailApiResponse;
    const { order_details, transactions } = detail;
    // Backend returns order_breakdown/refunded_amount/remaining_amount/refunds
    // as TOP-LEVEL siblings of `order`, never nested inside it (see
    // src/types/order_detail.ts) — merge them onto `order` here, once, so
    // every read below resolves against real data instead of `undefined`.
    const order: TOrderWithId = mergeOrderDetail(detail);
    const currency = order.currency ?? 'CAD';
    // Empty string counts as "no alias" here too — always fall back to the raw id.
    const orderIdDisplay = order.reference_code || `#${order.order_id}`;

    // Only a BUSINESS account may block a customer — a consumer viewing
    // their own purchase (drill-through from their own transaction) must
    // never see this action. Explicit `=== BUSINESS` (not `!== INDIVIDUAL`,
    // which the /orders route itself uses) so this never shows while `user`
    // is still unresolved (undefined, before the user context loads) or for
    // any other non-individual account type that isn't actually a merchant —
    // intentionally stricter than the route gate since blocking is a
    // significant, order-scoped action. Anonymous/pending orders have no
    // linked customer yet, so there is nobody to block.
    const canBlockCustomer = user?.type === EUserType.BUSINESS && !!order.customer_id;

    const initials = order.customer_display_name
        ? nameInitials(order.customer_display_name)
        : handleInitials(order.customer_id ?? '');

    // The backend does not flip order.status to 'refunded'/'partially_refunded'
    // after a refund is issued — the order's own status can stay 'succeeded'
    // indefinitely. Derive refund state from the refund records themselves
    // (order.refunds), never from order.status. `refunds` is captured as its
    // own const so every downstream read (badge, block, footer) narrows
    // correctly — isRefunded alone is a plain boolean and doesn't narrow
    // order.refunds for TS at a later, separate expression.
    const refunds = order.refunds;
    const isRefunded = !!refunds && refunds.length > 0;
    const isFullyRefunded = isRefunded && Number(order.remaining_amount ?? '0') <= 0;
    const breakdown = order.order_breakdown ?? null;
    const orderTransactions = transactions as TTransaction[] | undefined;
    const mergedTxnRows = buildMergedTxnRows(orderTransactions);
    const firstRefundTxnId = firstRefundTransactionId(orderTransactions);

    // An order is refundable when it is captured (succeeded or partially
    // refunded) and still has a positive remaining amount. Never fall back to
    // `order.total` here — that would silently re-offer money that has
    // already been refunded whenever `remaining_amount` happens to be
    // missing. Matches the developer-portal Orders surface (Surface A) and
    // this file's own refund-summary row below, both of which rely on
    // `?? '0'` alone against the same data source every VIEW_ORDER caller
    // uses (apiOrderService.getOrderById).
    const isRefundable =
        !refundJustSucceeded &&
        (order.status === 'succeeded' || order.status === 'partially_refunded') &&
        Number(order.remaining_amount ?? '0') > 0;

    const handleRefundSuccess = async () => {
        // Gate the drawer immediately: until we have fresh authoritative order
        // data, no second refund can be initiated. A re-opened refund view mints
        // a NEW idempotency key, so a stale drawer (unchanged remaining amount)
        // could otherwise issue a distinct, unintended second refund.
        setRefundJustSucceeded(true);
        setIsRefundOpen(false);
        showToast.success('Refund issued successfully.');
        try {
            const updated = await apiOrderService.getOrderById(order.id);
            changeModalData({
                order: updated,
                refetchOrders: modalData?.refetchOrders
            });
            // Fresh data is authoritative again — re-enable per the real
            // remaining amount so further partial refunds remain possible.
            setRefundJustSucceeded(false);
        } catch {
            // Re-fetch failed — keep the Refund button disabled so the stale
            // drawer cannot initiate a distinct second refund. Ask the user to
            // close and reopen (which remounts with fresh data).
            showToast.warning(
                'Refund issued, but the order could not be refreshed. Close and reopen to continue.'
            );
        }
        if (modalData?.refetchOrders) {
            modalData.refetchOrders();
        }
    };

    // Blocking is significant (the customer can no longer place new orders
    // with this business), so it goes through an in-drawer confirmation step
    // rather than firing on click — same "significant action needs
    // confirmation" bar as the delete-contact/delete-api-key modals, just as
    // a drawer subview (like Refund) instead of a separate registered modal.
    // A separate BLOCK_CUSTOMER modal was tried first, but onOpen/onClose
    // REPLACES the whole modal stack rather than layering on top of this
    // drawer, and handleCloseModal (fired on Cancel/X/overlay) unconditionally
    // clears modalData/modalState right after running any onClose callback —
    // so there was no way to cleanly return to this same order afterwards.
    // Swapping content in-place (same mechanism as RefundOrderSubview below)
    // sidesteps the modal stack entirely: nothing to clobber.
    const handleConfirmBlockCustomer = async () => {
        setIsBlockSubmitting(true);
        try {
            await apiOrderBlockService.blockCustomer(order.id);
            setIsBlockOpen(false);
            onClose();
            showToast.success('Customer blocked.');
            modalData?.refetchOrders?.();
        } catch (err) {
            const message =
                err instanceof OrderBlockApiError
                    ? err.message || 'Unable to block this customer. Please try again.'
                    : 'Unable to block this customer. Please try again.';
            // Swap back to Order details (not stay on the confirm step, not
            // close the drawer) so the user can retry or take another action
            // with full context still in front of them.
            setIsBlockOpen(false);
            showToast.error(message);
        } finally {
            setIsBlockSubmitting(false);
        }
    };

    return (
        <div className={styles.drawer}>
            {/* Refund renders as an in-drawer step (not a separate modal) — same
                back/close pattern as ReportProblemSubview in view_transaction. */}
            {isRefundOpen ? (
                <RefundOrderSubview
                    orderId={order.id}
                    // Same rule as isRefundable above — never fall back to
                    // order.total, or a stale/incomplete order snapshot could
                    // offer the full original amount as refundable.
                    availableToRefund={Number(order.remaining_amount ?? '0')}
                    currency={currency}
                    onBack={() => setIsRefundOpen(false)}
                    onClose={onClose}
                    onSuccess={handleRefundSuccess}
                />
            ) : isBlockOpen ? (
                <BlockCustomerSubview
                    customerName={order.customer_display_name || `@${order.customer_id}`}
                    isSubmitting={isBlockSubmitting}
                    onBack={() => setIsBlockOpen(false)}
                    onClose={onClose}
                    onConfirm={handleConfirmBlockCustomer}
                />
            ) : (
                <>
                    {/* Header */}
                    <div className={styles.drawer_header}>
                        <Typography variant="h4" weight="bold">
                            Order details
                        </Typography>
                        <button
                            type="button"
                            className={styles.close_btn}
                            onClick={onClose}
                            aria-label="Close">
                            <Icon name="close" width={18} height={18} />
                        </button>
                    </div>

                    {/* Scrollable body */}
                    <div className={styles.drawer_body}>
                        {/* User card */}
                        <div className={styles.user_card}>
                            {canBlockCustomer && (
                                // Own wrapper for absolute positioning — Dropdown's own
                                // `.wrapper` class sets `position: relative`, so passing
                                // `styles.kebab` (position: absolute) straight into its
                                // className would fight that rule on the same element,
                                // with the winner depending on unpredictable CSS bundle
                                // order. A separate wrapper avoids the conflict entirely.
                                <div className={styles.kebab}>
                                    <Dropdown
                                        items={[
                                            {
                                                label: submitLocale('block_customer'),
                                                icon: 'block',
                                                isRed: true,
                                                onClick: () => setIsBlockOpen(true)
                                            }
                                        ]}
                                    />
                                </div>
                            )}
                            <div className={styles.avatar_row}>
                                <div className={styles.avatar}>
                                    {order.customer_avatar_url && !imgError ? (
                                        <img
                                            src={order.customer_avatar_url}
                                            alt={order.customer_display_name || 'Customer'}
                                            className={styles.avatar_img}
                                            onError={() => setImgError(true)}
                                        />
                                    ) : (
                                        initials
                                    )}
                                </div>
                            </div>
                            {order.customer_display_name && (
                                <Typography
                                    variant="body2"
                                    weight="semibold"
                                    className={styles.customer_name}>
                                    {order.customer_display_name}
                                </Typography>
                            )}
                            <Typography variant="body4" className={styles.handle}>
                                @{order.customer_id}
                            </Typography>
                            <Typography variant="body4" className={styles.xeppt_label}>
                                XEPPT user
                            </Typography>
                            <div className={styles.pill_divider_row}>
                                <div className={styles.pill_line} />
                                <span className={styles.order_id_pill}>Order {orderIdDisplay}</span>
                                <div className={styles.pill_line} />
                            </div>
                            <Typography variant="h4" weight="bold" className={styles.big_amount}>
                                ${prettifyAmount(order.total)} {currency}
                            </Typography>
                            {/* Figma: variant "gray" (#e5e4e4/#282828) for both refund
                                states — matches Status's own `gray` variant exactly. */}
                            <Status variant={isRefunded ? 'gray' : orderStatusVariant(order.status)}>
                                {isRefunded
                                    ? isFullyRefunded
                                        ? 'Refunded'
                                        : 'Partially refunded'
                                    : orderStatusLabel(order.status)}
                            </Status>
                        </div>

                        {/* Tabs */}
                        <div className={styles.tabs_bar}>
                            {ORDER_TABS.map((tab) => (
                                <button
                                    key={tab.value}
                                    type="button"
                                    className={[
                                        styles.tab_btn,
                                        activeTab === tab.value ? styles.tab_btn_active : ''
                                    ].join(' ')}
                                    onClick={() => setActiveTab(tab.value)}>
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        {/* Tab: Details */}
                        {activeTab === 'details' && (
                            <div className={styles.detail_rows}>
                                <div className={styles.detail_row}>
                                    <span className={styles.detail_label}>Date and time</span>
                                    <span className={styles.detail_value}>
                                        {formatDate(order.created_at, 'DD MMMM YYYY, HH:mm')}
                                    </span>
                                </div>
                                <div className={styles.detail_row}>
                                    <span className={styles.detail_label}>Total amount</span>
                                    <span
                                        className={[
                                            styles.detail_value,
                                            styles.detail_value_bold
                                        ].join(' ')}>
                                        ${prettifyAmount(order.total)} {currency}
                                    </span>
                                </div>
                                <div className={styles.detail_row}>
                                    <span className={styles.detail_label}>Order ID</span>
                                    <span className={styles.detail_value}>{orderIdDisplay}</span>
                                </div>
                                {order.description && (
                                    <div className={styles.detail_row}>
                                        <span className={styles.detail_label}>Description</span>
                                        <span className={styles.detail_value}>
                                            {order.description}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Tab: Items */}
                        {activeTab === 'items' &&
                            (order_details && order_details.length > 0 ? (
                                <div className={styles.items_list}>
                                    <div className={styles.items_header_row}>
                                        <span className={styles.items_col_item}>Item</span>
                                        <span className={styles.items_col_qty}>Qty</span>
                                        <span className={styles.items_col_price}>Price</span>
                                    </div>
                                    {(order_details as TOrderDetailWithDesc[]).map((item, idx) => (
                                        <div key={item.sku || idx} className={styles.item_row}>
                                            <div className={styles.item_info}>
                                                <p className={styles.item_title}>{item.title}</p>
                                                {item.description && (
                                                    <p className={styles.item_desc}>{item.description}</p>
                                                )}
                                            </div>
                                            <span className={styles.item_qty}>x{item.quantity}</span>
                                            <span className={styles.item_price}>
                                                ${prettifyAmount(String(item.price))}
                                            </span>
                                        </div>
                                    ))}
                                    <div className={styles.items_breakdown}>
                                        <div className={styles.breakdown_row}>
                                            <span className={styles.breakdown_label}>Subtotal</span>
                                            <span className={styles.breakdown_value}>
                                                ${prettifyAmount(breakdown?.subtotal ?? order.total)}
                                            </span>
                                        </div>
                                        <div className={styles.breakdown_row}>
                                            <span className={styles.breakdown_label}>Shipping</span>
                                            <span className={styles.breakdown_value}>
                                                {breakdown
                                                    ? Number(breakdown.shipping) === 0
                                                        ? 'Free'
                                                        : `$${prettifyAmount(breakdown.shipping)}`
                                                    : 'Free'}
                                            </span>
                                        </div>
                                        {breakdown && (
                                            <div className={styles.breakdown_row}>
                                                <span className={styles.breakdown_label}>Fees</span>
                                                <span className={styles.breakdown_value}>
                                                    ${prettifyAmount(breakdown.fees)}
                                                </span>
                                            </div>
                                        )}
                                        <div
                                            className={[
                                                styles.breakdown_row,
                                                styles.breakdown_total_row
                                            ].join(' ')}>
                                            <span className={styles.breakdown_total_label}>Total</span>
                                            <span className={styles.breakdown_total_value}>
                                                ${prettifyAmount(breakdown?.total ?? order.total)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className={styles.tab_empty}>
                                    <Typography variant="body4">No items on this order.</Typography>
                                </div>
                            ))}

                        {/* Tab: Transactions — the refund summary (when this order
                            has refunds) followed by one merged, dated line-item
                            list (purchase + every refund), per the Figma spec. */}
                        {activeTab === 'transactions' && (
                            <>
                                {refunds && refunds.length > 0 && (
                                    // Figma: banner text + Original/Refunded/Remaining table
                                    // live inside ONE grey card (single Message instance),
                                    // not two stacked blocks.
                                    <div className={styles.refund_banner}>
                                        <Icon name="info" width={24} height={24} />
                                        <div className={styles.refund_banner_lines}>
                                            <Typography
                                                variant="body4"
                                                className={styles.refund_banner_text}>
                                                {refunds.length > 1 ? (
                                                    'This order has been refunded multiple times.'
                                                ) : (
                                                    <>
                                                        {'This order was refunded on '}
                                                        <strong>
                                                            {formatDate(
                                                                refunds[0].created_at,
                                                                'DD MMMM YYYY'
                                                            )}
                                                        </strong>
                                                        {'.  '}
                                                        <button
                                                            type="button"
                                                            className={styles.refund_banner_link}
                                                            onClick={() =>
                                                                setPendingTxnId(firstRefundTxnId ?? '')
                                                            }>
                                                            View refund
                                                        </button>
                                                    </>
                                                )}
                                            </Typography>
                                            <div className={styles.refund_summary}>
                                                <div className={styles.refund_summary_row}>
                                                    <span className={styles.refund_summary_label}>
                                                        Original amount:
                                                    </span>
                                                    <span className={styles.refund_summary_value}>
                                                        ${prettifyAmount(order.total)} {currency}
                                                    </span>
                                                </div>
                                                <div className={styles.refund_summary_row}>
                                                    <span className={styles.refund_summary_label}>
                                                        Refunded:
                                                    </span>
                                                    <span className={styles.refund_summary_value}>
                                                        ${prettifyAmount(order.refunded_amount ?? '0')}{' '}
                                                        {currency}
                                                    </span>
                                                </div>
                                                <div className={styles.refund_summary_row}>
                                                    <span className={styles.refund_summary_label}>
                                                        Remaining amount:
                                                    </span>
                                                    <span className={styles.refund_summary_value}>
                                                        ${prettifyAmount(order.remaining_amount ?? '0')}{' '}
                                                        {currency}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {mergedTxnRows.length > 0 ? (
                                    <div className={styles.txn_list}>
                                        {mergedTxnRows.map((row) => (
                                            <button
                                                key={row.id}
                                                type="button"
                                                className={styles.txn_row}
                                                onClick={() => setPendingTxnId(row.id)}>
                                                <span className={styles.txn_date}>
                                                    {formatDate(row.created_at, 'DD MMM YYYY')}
                                                </span>
                                                <span
                                                    className={[
                                                        styles.txn_amount,
                                                        row.colorClass
                                                    ].join(' ')}>
                                                    {row.amountLabel}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                ) : (
                                    <div className={styles.tab_empty}>
                                        <Typography variant="body4">
                                            No transactions on this order.
                                        </Typography>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Footer */}
                    <div className={styles.drawer_footer}>
                        <Button
                            variant="outlined-red"
                            size="medium"
                            className={styles.footer_btn}
                            onClick={() => handleOpenSubmitRequestModal(order.order_id ?? '')}>
                            Report problem
                        </Button>
                        {/* Only Report problem once fully refunded (remaining <= 0) —
                            driven off the derived refund state, never order.status
                            (the backend never sets it to 'refunded'). */}
                        {!isFullyRefunded && (
                            <Button
                                variant="outlined-primary"
                                size="medium"
                                className={styles.footer_btn}
                                disabled={!isRefundable}
                                onClick={isRefundable ? () => setIsRefundOpen(true) : undefined}>
                                Refund
                            </Button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default ViewOrderModal;
